/**
 * BlockersTwoColumn
 * Two-column grid layout for "What's Blocking" and "Who Can Unblock"
 * Stacks on narrow screens
 */

import { makeStyles, tokens, Text, Button, Avatar } from '@fluentui/react-components'
import { LockClosedRegular, PersonRegular, ChatRegular } from '@fluentui/react-icons'
import type { BlockerInfo, UnblockContact } from '../../../domain'

const useStyles = makeStyles({
  root: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingHorizontalL,
    '@media (max-width: 600px)': {
      gridTemplateColumns: '1fr',
    },
  },
  column: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    padding: tokens.spacingVerticalM,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  columnHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    marginBottom: tokens.spacingVerticalXS,
  },
  headerIcon: {
    color: tokens.colorNeutralForeground2,
    fontSize: '16px',
  },
  headerTitle: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  blockerItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXXS,
  },
  blockerDuration: {
    color: tokens.colorPaletteRedForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  blockerDescription: {
    color: tokens.colorNeutralForeground2,
  },
  contactItem: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  contactInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  contactDetails: {
    display: 'flex',
    flexDirection: 'column',
  },
  contactName: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  contactRole: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  messageButton: {
    alignSelf: 'flex-start',
  },
})

export interface BlockersTwoColumnProps {
  blockers: BlockerInfo[]
  unblockers: UnblockContact[]
  onMessageContact?: (contact: UnblockContact) => void
}

export function BlockersTwoColumn({ blockers, unblockers, onMessageContact }: BlockersTwoColumnProps) {
  const styles = useStyles()

  if (blockers.length === 0 && unblockers.length === 0) {
    return null
  }

  return (
    <div className={styles.root}>
      {/* What's Blocking */}
      <div className={styles.column}>
        <div className={styles.columnHeader}>
          <LockClosedRegular className={styles.headerIcon} />
          <Text className={styles.headerTitle}>What's Blocking</Text>
        </div>
        {blockers.map((blocker, index) => (
          <div key={index} className={styles.blockerItem}>
            <Text size={200} className={styles.blockerDuration}>
              Blocked {blocker.duration}
            </Text>
            <Text size={200} className={styles.blockerDescription}>
              {blocker.description}
            </Text>
          </div>
        ))}
        {blockers.length === 0 && (
          <Text size={200} className={styles.blockerDescription}>
            No blockers identified
          </Text>
        )}
      </div>

      {/* Who Can Unblock */}
      <div className={styles.column}>
        <div className={styles.columnHeader}>
          <PersonRegular className={styles.headerIcon} />
          <Text className={styles.headerTitle}>Who Can Unblock</Text>
        </div>
        {unblockers.map((contact) => (
          <div key={contact.id} className={styles.contactItem}>
            <div className={styles.contactInfo}>
              <Avatar name={contact.displayName} size={28} />
              <div className={styles.contactDetails}>
                <Text size={200} className={styles.contactName}>
                  {contact.displayName}
                </Text>
                {contact.role && (
                  <Text size={100} className={styles.contactRole}>
                    {contact.role}
                  </Text>
                )}
              </div>
            </div>
            <Button
              size="small"
              appearance="subtle"
              icon={<ChatRegular />}
              className={styles.messageButton}
              onClick={() => onMessageContact?.(contact)}
            >
              Message with context
            </Button>
          </div>
        ))}
        {unblockers.length === 0 && (
          <Text size={200} className={styles.blockerDescription}>
            No contacts identified
          </Text>
        )}
      </div>
    </div>
  )
}
